/**
 * ProtectedRoute Component
 * ========================
 * Route wrapper that requires authentication.
 * Redirects to login page if user is not authenticated.
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { AUTH_CONFIG } from '../../utils/constants';

/**
 * ProtectedRoute Component
 * Wraps protected routes and handles authentication check.
 * 
 * @param {object} props - Component props
 * @param {React.ReactNode} props.children - Child components (protected content)
 * @param {string} [props.redirectTo] - Path to redirect to if not authenticated
 * @param {boolean} [props.requiresAdmin] - Whether admin role is required
 * @returns {JSX.Element} Protected content or redirect
 */
export function ProtectedRoute({ 
  children, 
  redirectTo = AUTH_CONFIG.LOGIN_PATH,
  requiresAdmin = false,
}) {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();
  
  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-dme-blue-800">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-dme-orange border-t-transparent rounded-full animate-spin" />
          <p className="text-white text-sm">Loading...</p>
        </div>
      </div>
    );
  }
  
  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    // Save the attempted URL for redirect after login
    return (
      <Navigate 
        to={redirectTo} 
        state={{ from: location.pathname }} 
        replace 
      />
    );
  }
  
  // Check admin requirement
  if (requiresAdmin && user?.role !== 'admin') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-dme-blue-800">
        <div className="text-center text-white">
          <h1 className="text-4xl font-bold mb-4">Access Denied</h1>
          <p className="text-dme-gray-300 mb-6">
            You don't have permission to access this page.
          </p>
          <button
            onClick={() => window.history.back()}
            className="px-6 py-3 bg-dme-orange text-white rounded-lg hover:bg-dme-orange-dark transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }
  
  // Render protected content
  return children;
}

export default ProtectedRoute;
